import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useEventSource } from '@hooks/useEventSource';
import { useAuthStore } from '@stores/auth';
import { tokenStorage } from '@lib/auth/storage';
import { notificationsApi, type NotificationDoc } from '../api/notificationsApi';

export interface UseNotificationsStreamOptions {
  enabled?: boolean;
}

interface NotificationEvent {
  owner?: string;
  payload?: NotificationDoc;
}

/**
 * Application-wide subscription to the `notifications` SSE channel.
 *
 * Mount this ONCE inside the authenticated `AppShell` (alongside
 * `usePortalJobsStream`). On every push:
 *   1. show a `toast.info(title)` localised to the active language,
 *   2. invalidate the unread-count badge,
 *   3. invalidate the list query so the bell dropdown / Notification Center
 *      stay fresh without each component opening its own EventSource (browsers
 *      cap concurrent EventSource connections per origin).
 *
 * Token rotation: the EventSource URL embeds the JWT (cannot send headers),
 * so we poll `tokenStorage` every 30s and force-reconnect via `connectionKey`
 * whenever the access token changes — identical pattern to
 * `usePortalJobsStream`.
 */
export function useNotificationsStream(options: UseNotificationsStreamOptions = {}) {
  const { enabled = true } = options;
  const qc = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';

  const [token, setToken] = useState<string | null>(() => tokenStorage.getAccessToken());
  useEffect(() => {
    setToken(tokenStorage.getAccessToken());
    const id = setInterval(() => {
      const current = tokenStorage.getAccessToken();
      setToken((prev) => (prev === current ? prev : current));
    }, 30_000);
    return () => clearInterval(id);
  }, [isAuthenticated]);

  const url = enabled && isAuthenticated && token ? notificationsApi.streamUrl() : null;

  const { status } = useEventSource(url, {
    enabled: enabled && isAuthenticated,
    connectionKey: token,
    events: ['notifications'],
    onEvent: (_type, data) => {
      const evt = data as NotificationEvent | string | null;
      if (!evt || typeof evt !== 'object') return;
      const payload = evt.payload;
      if (payload) {
        const title = isRtl ? payload.title_ar : payload.title_en;
        const body = isRtl ? payload.body_ar : payload.body_en;
        if (title) {
          toast.info(title, body ? { description: body } : undefined);
        }
      }
      void qc.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
      void qc.invalidateQueries({ queryKey: ['notifications', 'list'] });
    },
  });

  return { status };
}
